import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface NotificationItem {
  id: string
  title: string
  description: string
  timestamp: number
  time?: string
  read: boolean
  type: 'document' | 'warning' | 'info' | 'success'
}

/**
 * Calcula el tiempo relativo dinámico (ej: "Ahora mismo", "Hace 5 min", "Hace 2 h", "Ayer", "Hace 3 días")
 */
export function getRelativeTime(timestampOrTime?: number | string): string {
  if (!timestampOrTime) return 'Ahora mismo'

  let timestamp: number
  if (typeof timestampOrTime === 'number') {
    timestamp = timestampOrTime
  } else {
    const parsed = Date.parse(timestampOrTime)
    if (!isNaN(parsed)) {
      timestamp = parsed
    } else {
      return timestampOrTime
    }
  }

  const now = Date.now()
  const diffInSeconds = Math.floor((now - timestamp) / 1000)

  if (diffInSeconds < 45) {
    return 'Ahora mismo'
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return `Hace ${diffInMinutes} min`
  }

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return `Hace ${diffInHours} h`
  }

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays === 1) {
    return 'Ayer'
  }
  if (diffInDays < 7) {
    return `Hace ${diffInDays} días`
  }

  const date = new Date(timestamp)
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
}

interface NotificationsState {
  items: NotificationItem[]
}

// Cargar notificaciones persistidas en localStorage si existen
const STORAGE_KEY = 'web_app_notifications'

function loadSavedNotifications(): NotificationItem[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved ? JSON.parse(saved) : []
  } catch (e) {
    return []
  }
}

function saveNotifications(items: NotificationItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch (e) {
    // Ignorar errores de quota en localStorage
  }
}

const initialState: NotificationsState = {
  items: loadSavedNotifications(),
}

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (
      state,
      action: PayloadAction<{
        id?: string
        title: string
        description: string
        type?: 'document' | 'warning' | 'info' | 'success'
        timestamp?: number
        time?: string
      }>
    ) => {
      const newItem: NotificationItem = {
        id: action.payload.id || crypto.randomUUID(),
        title: action.payload.title,
        description: action.payload.description,
        timestamp: action.payload.timestamp || Date.now(),
        read: false,
        type: action.payload.type || 'info',
      }
      state.items.unshift(newItem)
      const plainItems = JSON.parse(JSON.stringify(state.items))
      saveNotifications(plainItems)
    },
    markAllAsRead: (state) => {
      state.items.forEach((item) => {
        item.read = true
      })
      saveNotifications(state.items)
    },
    toggleRead: (state, action: PayloadAction<string>) => {
      const item = state.items.find((n) => n.id === action.payload)
      if (item) {
        item.read = !item.read
        saveNotifications(state.items)
      }
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((n) => n.id !== action.payload)
      saveNotifications(state.items)
    },
    clearAllNotifications: (state) => {
      state.items = []
      saveNotifications(state.items)
    },
  },
})

export const {
  addNotification,
  markAllAsRead,
  toggleRead,
  removeNotification,
  clearAllNotifications,
} = notificationsSlice.actions

export default notificationsSlice.reducer
